<template>
  <div>
    <SubscriptionDetailsCard />
    <v-row>
      <v-col cols="12">
          <h2 class="mb-3 mt-4">{{ $t('console.tenantSettings')}}</h2>
          <v-form ref="form" v-on:submit.prevent="formSubmit" v-bind:disabled="isFormDisabled" autocomplete="off">
            <v-card elevation=0 class="pa-4">
              <div class="settings-info">{{ $t('menus.emailAddresses.settingsInfo') }}</div>

              <v-row>
                <v-col cols="12" md="4"  class="d-flex align-center">
                  {{ $t('menus.emailAddresses.emailEditorial') }}
                </v-col>
                <v-col cols="10" md="6" lg="6" xl=4 class="d-flex align-center">
                  <v-text-field
                      v-model="emailEditorial"
                      v-bind:rules="emailEditorialRules"
                      v-bind:error-messages="emailEditorialErrors"
                      required block
                      hide-details="auto"
                      variant="solo-filled" flat>
                  </v-text-field>
                </v-col>
                <v-col cols="2" class="d-flex align-center">
                  <v-tooltip location="bottom">
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props">mdi-information-outline</v-icon>
                    </template>
                    <div>
                      {{ $t('menus.emailAddresses.emailInfo') }}
                      <ul class="pl-4">
                        <li>{{ $t('menus.emailAddresses.projectStatusChanged') }}</li>
                      </ul>
                    </div>
                  </v-tooltip>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" md="4"  class="d-flex align-center">
                  {{ $t('menus.emailAddresses.emailTraffic') }}
                </v-col>
                <v-col cols="10" md="6" lg="6" xl=4 class="d-flex align-center">
                  <v-text-field
                      v-model="emailTraffic"
                      v-bind:rules="emailTrafficRules"
                      v-bind:error-messages="emailTrafficErrors"
                      required block
                      hide-details="auto"
                      variant="solo-filled" flat>
                  </v-text-field>
                </v-col>
                <v-col cols="2" class="d-flex align-center">
                  <v-tooltip location="bottom">
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props">mdi-information-outline</v-icon>
                    </template>
                    <div>
                      {{ $t('menus.emailAddresses.emailInfo') }}
                      <ul class="pl-4">
                        <li>{{ $t('menus.emailAddresses.tenantTraffic') }}</li>
                      </ul>
                    </div>
                  </v-tooltip>
                </v-col>
              </v-row>

              <v-row>
                <v-col cols="12" md="4"  class="d-flex align-center">
                  {{ $t('menus.emailAddresses.emailSupport') }}
                </v-col>
                <v-col cols="10" md="6" lg="6" xl=4 class="d-flex align-center">
                  <v-text-field
                      v-model="emailSupport"
                      v-bind:rules="emailSupportRules"
                      v-bind:error-messages="emailSupportErrors"
                      required block
                      hide-details="auto"
                      variant="solo-filled" flat>
                  </v-text-field>
                </v-col>
                <v-col cols="2" class="d-flex align-center">
                  <v-tooltip location="bottom">
                    <template v-slot:activator="{ props }">
                      <v-icon v-bind="props">mdi-information-outline</v-icon>
                    </template>
                    <div>
                      {{ $t('menus.emailAddresses.emailInfo') }}
                      <ul class="pl-4">
                        <li>{{ $t('menus.emailAddresses.technicalSupport') }}</li>
                      </ul>
                    </div>
                  </v-tooltip>
                </v-col>
              </v-row>

              <v-row>
                <v-col>
                  <div v-if="errorMessage" class="bg-error">
                    {{errorMessage}}
                  </div>
                  <div v-if="successMessage" class="bg-success">
                    {{successMessage}}
                  </div>
                  <div v-if="errorTranslatable" class="bg-error">
                    {{$replaceT(errorTranslatable.code, errorTranslatable.params, errorTranslatable.content)}}
                  </div>
                  <div v-if="successTranslatable" class="bg-success">
                    {{$replaceT(successTranslatable.code, successTranslatable.params, successTranslatable.content)}}
                  </div>
                </v-col>
              </v-row>


              <v-card-actions>
                <v-btn type="submit" class="bg-primary" elevation=0>
                  {{ $t('menus.emailAddresses.saveChanges') }}
                </v-btn>
              </v-card-actions>
            </v-card>
          </v-form>
      </v-col>
    </v-row>

    <v-row v-if="$canControlTenantConfig()">
      <v-col cols="12">
        <v-card elevation=0 class="pa-4">
          <v-row>
            <v-col>
              <h3>{{ $t('console.videoTranscodingResolutions') }}</h3>
              <p class="mb-0">{{ $t('console.videoTranscodingResolutionsFutureUploads') }}</p>
            </v-col>
          </v-row>

          <v-alert
            v-if="videoTranscodingResolutionsError"
            type="error"
            variant="tonal"
            aria-live="polite"
          >
            {{ videoTranscodingResolutionsError }}
          </v-alert>

          <v-progress-linear
            v-if="isLoadingVideoTranscodingResolutions"
            indeterminate
            color="primary"
            :aria-label="$t('console.videoTranscodingResolutionsLoading')"
          />

          <v-card
            v-for="profile in profiles"
            :key="profile.id"
            class="mb-2"
            variant="tonal"
          >
            <v-card-text class="px-3 py-2">
              <div class="d-flex align-center justify-space-between ga-2">
                <div>
                  <div class="text-subtitle-1 font-weight-medium">{{ profile.label }}</div>
                  <div class="text-body-2 text-medium-emphasis">
                    {{ $t('console.videoTranscodingResolutionOutput') }}
                  </div>
                </div>
                <v-switch
                  v-model="profile.enabled"
                  :aria-label="profile.label"
                  color="primary"
                  hide-details
                  density="compact"
                  inset
                />
              </div>

              <div class="d-flex flex-wrap align-center ga-2 mt-2">
                <v-chip color="primary" size="small" variant="tonal">
                  <v-icon start size="small">mdi-brain</v-icon>
                  {{ profile.creditsPerMinute === 0
                    ? $t('console.videoTranscodingResolutionFree')
                    : $t('console.videoTranscodingResolutionPrice', { credits: profile.creditsPerMinute }) }}
                </v-chip>
              </div>
            </v-card-text>
          </v-card>

          <v-card-actions>
            <v-btn
              class="bg-primary"
              :disabled="isLoadingVideoTranscodingResolutions || isSavingVideoTranscodingResolutions"
              :loading="isSavingVideoTranscodingResolutions"
              @click="saveVideoTranscodingResolutions()"
            >
              {{ $t('common.save') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-col>
    </v-row>
    <v-row>
      <v-col cols="12">
        <!-- <h2 class="mb-3 mt-4">{{ $t('console.tenantSettings')}}</h2> -->
        <v-card elevation=0 class="pa-4">
          <v-row>
            <v-col>
              <h3>{{ $t('console.shareLinkOptions') }}</h3>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="4"  class="d-flex align-center">
              <v-text-field
                ref="shareLinkExpiryInput"
                v-model="currentLinksExpiryDays"
                :rules="currentLinksExpiryDaysRules"
                :placeholder="$t('console.defaultExpiryDays')"
                @blur="saveLinkExpiryDays()"
                hide-details="auto"
                block
                variant="solo-filled"
                flat
              />
            </v-col>
            <v-col cols="10" md="6" lg="6" xl=4 class="d-flex align-center">
              {{ $t('console.defaultExpiryDays') }}
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="4" class="d-flex align-center">
              <v-switch
                v-model="currentLinksAllowDownloading"
                :label="$t('console.linksDefaultAllowDownloading')"
                hide-details="auto"
                color="primary"
                @update:modelValue="saveLinksAllowDownloading()"
              />
            </v-col>
            <v-col cols="10" md="6" lg="6" xl="4" class="d-flex align-center">
              {{ $t('console.linksDefaultAllowDownloadingDescription') }}
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="4" class="d-flex align-center">
              <v-switch
                v-model="currentLinksAllowComments"
                :label="$t('console.linksDefaultAllowComments')"
                hide-details="auto"
                color="primary"
                @update:modelValue="saveLinksAllowComments()"
              />
            </v-col>
            <v-col cols="10" md="6" lg="6" xl="4" class="d-flex align-center">
              {{ $t('console.linksDefaultAllowCommentsDescription') }}
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="4" class="d-flex align-center">
              <v-switch
                v-model="currentLinksShowAssets"
                :label="$t('console.linksDefaultShowAssets')"
                hide-details="auto"
                color="primary"
                @update:modelValue="saveLinksShowAssets()"
              />
            </v-col>
            <v-col cols="10" md="6" lg="6" xl="4" class="d-flex align-center">
              {{ $t('console.linksDefaultShowAssetsDescription') }}
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="4" class="d-flex align-center">
              <v-switch
                v-model="currentLinksShowSubtitles"
                :label="$t('console.linksDefaultShowSubtitles')"
                hide-details="auto"
                color="primary"
                @update:modelValue="saveLinksShowSubtitles()"
              />
            </v-col>
            <v-col cols="10" md="6" lg="6" xl="4" class="d-flex align-center">
              {{ $t('console.linksDefaultShowAssetsDescription') }}
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="4" class="d-flex align-center">
              <v-switch
                v-model="currentLinksAllowPlayingOriginalFile"
                :label="$t('console.linksDefaultAllowPlayingOriginalFile')"
                hide-details="auto"
                color="primary"
                @update:modelValue="saveLinksAllowPlayingOriginalFile()"
              />
            </v-col>
            <v-col cols="10" md="6" lg="6" xl="4" class="d-flex align-center">
              {{ $t('console.linksDefaultAllowPlayingOriginalFileDescription') }}
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="4" class="d-flex align-center">
              <v-switch
                v-model="currentLinksAddVersions"
                :label="$t('console.linksDefaultAddVersions')"
                hide-details="auto"
                color="primary"
                @update:modelValue="saveLinksAddVersions()"
              />
            </v-col>
            <v-col cols="10" md="6" lg="6" xl="4" class="d-flex align-center">
              {{ $t('console.linksDefaultAddVersionsDescription') }}
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>


    <v-row>
      <v-col cols="12">
        <v-card elevation=0 class="pa-4">



          <v-row>
            <v-col>
              <h3>{{ $t('console.recycleBinOptions') }}</h3>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="4"  class="d-flex align-center">
              <v-text-field
                ref="retentionPeriodInput"
                v-model="currentRetentionPeriod"
                :rules="currentRetentionPeriodRules"
                :placeholder="$t('console.recycleBinOptions')"
                @blur="saveRetentionPeriod()"
                hide-details="auto"
                block
                variant="solo-filled"
                flat
              />
            </v-col>
            <v-col cols="10" md="6" lg="6" xl=4 class="d-flex align-center">
              {{ $t('console.retentionPeriodDays') }}
            </v-col>
          </v-row>

          <v-row>
            <v-col>
              <h3>{{ $t('console.securityOptions') }}</h3>
            </v-col>
          </v-row>
          <v-row>
            <v-col cols="12" md="4"  class="d-flex align-center">
              <v-switch v-model="isMFAEnabled" color="primary">
                <template v-slot:label>
                  {{ isMFAEnabled ? $t('console.mfaDisable') : $t('console.mfaEnforce')}}
                </template>
              </v-switch>
            </v-col>

            <v-col cols="10" md="6" lg="6" xl=4 class="d-flex align-center">
              <div v-if="this.isMFAEnabled" class="mb-2">{{ $t('console.disableMFARequirement') }} </div>
              <div v-else class="mb-2">{{ $t('console.enableMFARequirement') }} </div>
            </v-col>
          </v-row>
        </v-card>
      </v-col>
    </v-row>

    <v-row v-if="getAreYouOwner">
      <v-col cols="12">
        <!-- <h2 class="mb-3 mt-4">{{ $t('console.tenantSettings')}}</h2> -->
        <v-card elevation=0 class="pa-4">
          <v-row>
            <v-col>
              <h3>{{ $t('console.dangerZone') }}</h3>
            </v-col>
          </v-row>

          <v-row>
            <v-col cols="12" md="6" class="d-flex justify-center">
              <v-btn elevation="0" class="bg-red-darken-3" variant="text" @click="showingDeleteTenantDialog = true">
                {{ $t('console.deleteOwnTenant') }}
              </v-btn>
            </v-col>
            <v-col cols="12" md="6" class="d-flex justify-center" @click="showingPromoteOwnerDialog = true">
              <v-btn elevation="0" color="primary">
                {{ $t('console.transferOwnership') }}
              </v-btn>
            </v-col>

            <DeleteTenantDialog v-model="showingDeleteTenantDialog">
            </DeleteTenantDialog>

            <PromoteOwnerDialog v-model="showingPromoteOwnerDialog">
            </PromoteOwnerDialog>
          </v-row>
        </v-card>
      </v-col>
    </v-row>

  </div>
</template>

<script>
import { useAuthStore } from '@/stores/AuthStore.js'
import { useCommonStore } from '@/stores/CommonStore.js'
import { useSystemStore } from '@/stores/SystemStore.js'
import { useTenantStore } from '@/stores/TenantStore.js'
import Helper from '@/components/helper.js';
import TenantCalls from '@/api/modules/TenantCalls.js';
import SubscriptionDetailsCard from './components/SubscriptionDetailsCard.vue';
import DeleteTenantDialog from './components/dialogs/DeleteTenantDialog.vue';
import PromoteOwnerDialog from './components/dialogs/PromoteOwnerDialog.vue';

export default {
  name: "SettingsIndex",
  components: {
    SubscriptionDetailsCard,
    DeleteTenantDialog,
    PromoteOwnerDialog,
  },
  data() {
    return {
      emailEditorial: "",
      emailEditorialErrors: [],
      emailEditorialRules: Helper.getEmailValidationRules(this),

      emailTraffic: "",
      emailTrafficErrors: [],
      emailTrafficRules: Helper.getEmailValidationRules(this),

      emailSupport: "",
      emailSupportErrors: [],
      emailSupportRules: Helper.getEmailValidationRules(this),

      errorMessage: '',
      errorTranslatable: null,
      successMessage: '',
      successTranslatable: null,

      currentRetentionPeriod: 90,
      currentRetentionPeriodRules: [
        (val) => !!Number(val) || this.$t('routes.recycleBin.invalidRetentionPeriod'),
        (val) => {
          const parsedValue = Number(val);
          return !Number.isSafeInteger(parsedValue) || parsedValue <= 0 || parsedValue > 180
            ? this.$t('routes.recycleBin.invalidRetentionPeriod')
            : true;
        }
      ],

      currentLinksExpiryDays: 14,
      currentLinksExpiryDaysRules: [
        (val) => {
          const parsedValue = Number(val);
          return !Number.isSafeInteger(parsedValue) || parsedValue <= 0 || parsedValue > 365
            ? this.$t('console.invalidSharelinkExpiryPeriod')
            : true;
        }
      ],

      currentLinksAllowDownloading: false,
      currentLinksAllowComments: false,
      currentLinksShowAssets: false,
      currentLinksShowSubtitles: false,
      currentLinksAllowPlayingOriginalFile: false,
      currentLinksAddVersions: false,
      profiles: [],
      savedEnabledProfileIds: [],
      isLoadingVideoTranscodingResolutions: false,
      isSavingVideoTranscodingResolutions: false,
      videoTranscodingResolutionsError: '',


      showingDeleteTenantDialog: false,
      showingPromoteOwnerDialog: false,
    };
  },
  watch: {
    getRecycleBinRetentionPeriod: function(newValue) {
      this.currentRetentionPeriod = newValue;
    },
    getTenantLinksExpiryDays: function(newValue) {
      this.currentLinksExpiryDays = newValue;
    },
    getLinksAllowDownloading: function (newValue) {
      this.currentLinksAllowDownloading = newValue;
    },
    getLinksAllowComments: function (newValue) {
      this.currentLinksAllowComments = newValue;
    },
    getLinksShowAssets: function (newValue) {
      this.currentLinksShowAssets = newValue;
    },
    getLinksShowSubtitles: function (newValue) {
      this.currentLinksShowSubtitles = newValue;
    },
    getLinksAllowPlayingOriginalFile: function (newValue) {
      this.currentLinksAllowPlayingOriginalFile = newValue;
    },
    getLinksAddVersions: function (newValue) {
      this.currentLinksAddVersions = newValue;
    },
  },
  mounted: function() {
    // If the user object is not available, fetch it from the database
    if (!this.user) {
      const self = this;

      const authStore = useAuthStore();
      authStore.refreshDBUser()
        .then((user) => {
          if (this.isRootUser) {
            this.$router.push('/console/index');
            return;
          }

          self.refreshEmailAddresses(user);
          self.initializeVideoTranscodingResolutions();
          return user;
        })
        .catch((error) => {
          if (error && error.response && error.response.data && error.response.data.error) {
            const localizedMessage = self.$localizeMessage(error.response.data.error, self.$t('backend.system-error'));
            self.$showMessage(localizedMessage, { type: 'error' });
          } else {
            console.log(error);
            self.$showMessage(self.$t('backend.system-error'), { type: 'error' });
          }
        });
    } else {
      if (this.isRootUser) {
        this.$router.push('/console/users');
        return;
      }

      this.refreshEmailAddresses(this.user);
      this.initializeVideoTranscodingResolutions();
    }

    this.refreshMFAEnabled();
    this.refreshTenantSettings();
    this.currentRetentionPeriod = this.getRecycleBinRetentionPeriod;
    this.currentLinksExpiryDays = this.getTenantLinksExpiryDays;

    this.currentLinksAllowDownloading = this.getLinksAllowDownloading;
    this.currentLinksAllowComments = this.getLinksAllowComments;
    this.currentLinksAllowPlayingOriginalFile = this.getLinksAllowPlayingOriginalFile;
    this.currentLinksAddVersions = this.getLinksAddVersions;
    this.currentLinksShowAssets = this.getLinksShowAssets;
    this.currentLinksShowSubtitles = this.getLinksShowSubtitles;
  },
  methods: {
    formSubmit: async function() {
      this.errorMessage = '';
      this.errorTranslatable = null;
      this.successMessage = '';
      this.successTranslatable = null;

      const { valid } = await this.$refs.form.validate();
      if (!valid || !this.rootNodeId) {
        return;
      }

      const payload = {
        contextNodeId: this.rootNodeId,
        body: {
          editorial: this.emailEditorial,
          traffic: this.emailTraffic,
          support: this.emailSupport,
        }
      };

      const systemStore = useSystemStore();

      try {
        const response = await systemStore.saveTenantEmails(payload);
        // this.$showMessage(this.$t('menus.emailAddresses.savedSuccessfully'));

        console.log("Response is ", response);
        if (response && response.data && response.data.message && response.data.message.code) {
          this.successTranslatable = response.data.message;
        }
      } catch (error) {
        if (error?.response?.data?.error?.message) {
          const localizedMessage = this.$localizeMessage(error.response.data.error.message, this.$t('backend.system-error'));
          this.$showMessage(localizedMessage, { type: 'error' });
        } else {
          console.log(error);
          this.$showMessage(this.$t('backend.system-error'), { type: 'error' });
        }
      }
    },
    refreshEmailAddresses: function() {
      let self = this;

      const systemStore = useSystemStore();
      systemStore.getTenantEmails(this.rootNodeId)
        .then(function(response) {
          self.currentNodeValid = true;
          self.emailEditorial = response.data.emails.email_editorial || "";
          self.emailTraffic = response.data.emails.email_traffic || "";
          self.emailSupport = response.data.emails.email_support || "";
        })
        .catch(function(error) {
          if (error && error.response && error.response.data && error.response.data.error) {
            const localizedMessage = self.$localizeMessage(error.response.data.error, self.$t('backend.system-error'));
            self.$showMessage(localizedMessage, { type: 'error' });
          } else {
            console.log(error);
            self.$showMessage(self.$t('backend.system-error'), { type: 'error' });
          }
        });
    },

    toggleMFA: function() {
      this.errorMessage = '';
      this.errorTranslatable = null;
      this.successMessage = '';
      this.successTranslatable = null;

      let payload = {
        mfarequired: !this.isMFAEnabled,
        accessPointId: this.rootNodeId,
      }

      const systemStore = useSystemStore();
      systemStore.updateTenantMFA(payload)
      .then(() => {
        this.$showMessage(payload.mfarequired ? this.$t('menus.setMFA.enabledSuccessfully') : this.$t('menus.setMFA.disabledSuccessfully'));
        this.refreshMFAEnabled();
      })
      .catch((error) => {
        if (error && error.message) {
          this.$showMessage(error.message, {type: 'error'});
        }

        console.error(error);
      });
    },
    refreshMFAEnabled: function() {
      const authStore = useAuthStore();
      authStore.refreshDBUser();
    },

    refreshTenantSettings: function() {
      const tenantStore = useTenantStore();
      tenantStore.refreshTenantSettings(this.rootNodeId);
    },
    initializeVideoTranscodingResolutions: function() {
      if (!this.$canControlTenantConfig() || !this.rootNodeId) {
        return;
      }

      this.loadVideoTranscodingResolutions();
    },
    loadVideoTranscodingResolutions: async function() {
      this.isLoadingVideoTranscodingResolutions = true;
      this.videoTranscodingResolutionsError = '';

      try {
        const response = await TenantCalls.getVideoTranscodingResolutions(this.rootNodeId);
        this.profiles = response.data.profiles;
        this.savedEnabledProfileIds = this.profiles
          .filter((profile) => profile.enabled)
          .map((profile) => profile.id);
      } catch {
        this.videoTranscodingResolutionsError = this.$t('console.videoTranscodingResolutionsLoadError');
      } finally {
        this.isLoadingVideoTranscodingResolutions = false;
      }
    },
    saveVideoTranscodingResolutions: async function() {
      const enabledProfileIds = this.profiles
        .filter((profile) => profile.enabled)
        .map((profile) => profile.id);

      if (enabledProfileIds.length === 0) {
        this.videoTranscodingResolutionsError = this.$t('console.videoTranscodingResolutionsSelectionRequired');
        this.profiles.forEach((profile) => {
          profile.enabled = this.savedEnabledProfileIds.includes(profile.id);
        });
        return;
      }

      this.isSavingVideoTranscodingResolutions = true;
      this.videoTranscodingResolutionsError = '';

      try {
        const response = await TenantCalls.updateVideoTranscodingResolutions(
          this.rootNodeId,
          enabledProfileIds,
        );
        this.profiles = response.data.profiles;
        this.savedEnabledProfileIds = this.profiles
          .filter((profile) => profile.enabled)
          .map((profile) => profile.id);
        this.$showMessage(this.$t('console.videoTranscodingResolutionsSaveSuccess'));
      } catch (error) {
        if (error?.response?.status === 400) {
          this.profiles.forEach((profile) => {
            profile.enabled = this.savedEnabledProfileIds.includes(profile.id);
          });
        }

        this.videoTranscodingResolutionsError = this.$t('console.videoTranscodingResolutionsSaveError');
      } finally {
        this.isSavingVideoTranscodingResolutions = false;
      }
    },
    saveRetentionPeriod: async function () {
      const validationErrors = await this.$refs.retentionPeriodInput.validate();
      if (validationErrors.length > 0) {
        return;
      }

      const retentionPeriod = Number(this.currentRetentionPeriod);

      try {
        const tenantStore = useTenantStore();
        await tenantStore.updateTenantSettings({
          accessPointId: this.rootNodeId,
          settings: {
            recycle_bin_retention_period: retentionPeriod,
          },
        });

        this.$showMessage(this.$t('console.retentionPeriodUpdated'));
      } catch (error) {
        if (error && error.response && error.response.data && error.response.data.error) {
          this.errorMessage = error.response.data.error.message;
        } else {
          this.errorMessage = this.$t('common.generalError');
        }
      }
    },
    saveLinkExpiryDays: async function () {
      const validationErrors = await this.$refs.shareLinkExpiryInput.validate();
      if (validationErrors.length > 0) {
        return;
      }

      const linksExpiryDays = Number(this.currentLinksExpiryDays);

      try {
        const tenantStore = useTenantStore();
        await tenantStore.updateTenantSettings({
          accessPointId: this.rootNodeId,
          settings: {
            links_expiry_days: linksExpiryDays,
          },
        });

        this.$showMessage(this.$t('console.linksExpiryDaysUpdated'));
      } catch (error) {
        if (error && error.response && error.response.data && error.response.data.error) {
          this.errorMessage = error.response.data.error.message;
        } else {
          this.errorMessage = this.$t('common.generalError');
        }
      }
    },

    updateBooleanOption: async function (name, currentValue, successMessage) {
      const tenantStore = useTenantStore();

      try {
        await tenantStore.updateTenantSettings({
          accessPointId: this.rootNodeId,
          settings: {
            [name]: currentValue,
          }
        });

        this.$showMessage(successMessage);
      } catch (error) {
        if (error && error.response && error.response.data && error.response.data.error) {
          this.errorMessage = error.response.data.error.message;
        } else {
          this.errorMessage = this.$t('common.generalError');
        }
      }
    },

    saveLinksAllowDownloading: function () {
      return this.updateBooleanOption(
        'links_allow_downloading',
        this.currentLinksAllowDownloading,
        this.$t('console.linksDefaultAllowDownloadingUpdated'),
      );
    },
    saveLinksAllowComments: function () {
      return this.updateBooleanOption(
        'links_allow_comments',
        this.currentLinksAllowComments,
        this.$t('console.linksDefaultAllowCommentsUpdated'),
      );
    },
    saveLinksShowAssets: function () {
      return this.updateBooleanOption(
        'links_show_assets',
        this.currentLinksShowAssets,
        this.$t('console.linksDefaultShowAssetsUpdated'),
      );
    },
    saveLinksShowSubtitles: function () {
      return this.updateBooleanOption(
        'links_show_subtitles',
        this.currentLinksShowSubtitles,
        this.$t('console.linksDefaultShowSubtitlesUpdated'),
      )
    },
    saveLinksAllowPlayingOriginalFile: function () {
      return this.updateBooleanOption(
        'links_allow_playing_original_file',
        this.currentLinksAllowPlayingOriginalFile,
        this.$t('console.linksDefaultAllowPlayingOriginalFileUpdated'),
      );
    },
    saveLinksAddVersions: function () {
      return this.updateBooleanOption(
        'links_add_versions',
        this.currentLinksAddVersions,
        this.$t('console.linksDefaultAddVersionsUpdated'),
      );
    }

  },
  computed: {
    user: function() {
      const authStore = useAuthStore();
      return authStore.dbUser;
    },
    rootNodeId: function() {
      const authStore = useAuthStore();
      return authStore.rootNodeId;
    },
    isFormDisabled: function() {
      return !this.rootNodeId;
    },
    isRootUser() {
      const commonStore = useCommonStore();
      const rootFolderId = commonStore.rootFolderId;

      return this.rootNodeId === rootFolderId;
    },

    getTenantLinksExpiryDays() {
      const tenantStore = useTenantStore();
      return tenantStore.getTenantLinksExpiryDays;
    },
    isMFAEnabled: {
      get() {
        const authStore = useAuthStore();
        const tenant = authStore.getActiveTenant;
        return tenant ? tenant.mfarequired : false;
      },
      set(value) {
        // This will be called when the switch is toggled
        // The actual logic is handled in toggleMFA
        this.toggleMFA(value);
      }
    },
    getRecycleBinRetentionPeriod() {
      const tenantStore = useTenantStore();
      return tenantStore.getTenantRetentionPeriod;
    },
    getLinksAllowDownloading() {
      const tenantStore = useTenantStore();
      return tenantStore.getLinksAllowDownloading;
    },
    getLinksAllowComments() {
      const tenantStore = useTenantStore();
      return tenantStore.getLinksAllowComments;
    },
    getLinksShowAssets() {
      const tenantStore = useTenantStore();
      return tenantStore.getLinksShowAssets;
    },
    getLinksAllowPlayingOriginalFile() {
      const tenantStore = useTenantStore();
      return tenantStore.getLinksAllowPlayingOriginalFile;
    },
    getLinksAddVersions() {
      const tenantStore = useTenantStore();
      return tenantStore.getLinksAddVersions;
    },
    getLinksShowSubtitles() {
      const tenantStore = useTenantStore();
      return tenantStore.getLinksShowSubtitles;
    },
    getAreYouOwner() {
      const tenantStore = useTenantStore();
      return tenantStore.getAreYouOwner;
    }
  }

}
</script>

<style>
.settings-info {
  margin-top: 20px;
  margin-bottom: 20px;
  font-size: 1.2rem;
}
</style>
